import React, { useState, useRef, useCallback, useMemo } from "react";
import { useTheme } from "../../contexts/Theme/ThemeContext";
import { ReactComponent as PlusSVG } from "../../assets/newImages/icons/plus.svg";
import { ReactComponent as TagDiamondSVG } from "../../assets/newImages/icons/tag-diamond.svg";
import { TagValueInputContainer } from "./styles";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  horizontalListSortingStrategy,
  useSortable,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

/** Cor ou foto de um valor de variação, vinda do dicionário da empresa. */
export interface SwatchVariacao {
  cor_hex?: string | null;
  imagem?: string | null;
}

export const chaveSwatch = (valor: string) =>
  (valor || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();

interface TagValueInputProps {
  values: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  helperText?: React.ReactNode;
  disabled?: boolean;
  label?: React.ReactNode;
  onClear?: () => void;
  dragHandleProps?: any;
  suggestions?: string[];
  /** Swatch por valor, indexado por chaveSwatch() (o dicionário ignora acento e caixa). */
  swatches?: Record<string, SwatchVariacao>;
  /** Clique no swatch abre o modal "Editar variação". Sem isso, o chip segue como antes. */
  onEditSwatch?: (value: string) => void;
}

interface SortableTagProps {
  id: string;
  value: string;
  disabled: boolean;
  onRemove: () => void;
  onEdit: (newValue: string) => void;
  swatch?: SwatchVariacao;
  onEditSwatch?: (value: string) => void;
}

const SortableTag: React.FC<SortableTagProps> = ({ id, value, disabled, onRemove, onEdit, swatch, onEditSwatch }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled });

  const [editing, setEditing] = React.useState(false);
  const [editValue, setEditValue] = React.useState(value);
  const editRef = React.useRef<HTMLInputElement>(null);
  /* Segura o clique simples no nome até saber se vira duplo clique (rename inline). */
  const clickTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  React.useEffect(() => {
    if (editing && editRef.current) {
      editRef.current.focus();
      editRef.current.select();
    }
  }, [editing]);

  React.useEffect(
    () => () => {
      if (clickTimer.current) clearTimeout(clickTimer.current);
    },
    []
  );

  const cancelPendingClick = () => {
    if (clickTimer.current) {
      clearTimeout(clickTimer.current);
      clickTimer.current = null;
    }
  };

  const commitEdit = () => {
    const trimmed = editValue.trim();
    if (trimmed && trimmed !== value) {
      onEdit(trimmed);
    } else {
      setEditValue(value);
    }
    setEditing(false);
  };

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="tag-chip fonteTexto"
      {...attributes}
    >
      {/*
        Este span acumula três papéis: drag handle do dnd-kit, swatch da variação e
        gatilho do modal. Clique e arraste convivem porque o PointerSensor exige 5px
        de movimento (ver sensors, mais abaixo) — clique parado nunca vira arraste.
        Aqui o clique abre o modal na hora; no tag-text ele espera o timer, porque lá
        o duplo clique renomeia inline e dispara dois cliques simples antes.
      */}
      <span
        {...listeners}
        style={{
          cursor: onEditSwatch ? "pointer" : disabled ? "default" : "grab",
          display: "flex",
          touchAction: "none",
        }}
        onClick={() => onEditSwatch?.(value)}
        title={onEditSwatch ? "Editar variação" : undefined}
      >
        {swatch?.imagem ? (
          <img className="tag-swatch" src={swatch.imagem} alt={value} />
        ) : swatch?.cor_hex ? (
          <span className="tag-swatch" style={{ backgroundColor: swatch.cor_hex }} />
        ) : (
          <TagDiamondSVG className="tag-diamond" />
        )}
      </span>
      {editing && !disabled ? (
        <input
          ref={editRef}
          className="tag-edit-input fonteTexto"
          value={editValue}
          size={Math.max(editValue.length, 5)}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={commitEdit}
          onKeyDown={(e) => {
            if (e.key === "Enter") commitEdit();
            if (e.key === "Escape") { setEditValue(value); setEditing(false); }
          }}
          onPointerDown={(e) => e.stopPropagation()}
        />
      ) : (
        <span
          className="tag-text"
          style={{ cursor: onEditSwatch ? "pointer" : undefined }}
          title={onEditSwatch ? "Editar variação" : undefined}
          onClick={() => {
            if (!onEditSwatch) return;
            cancelPendingClick();
            clickTimer.current = setTimeout(() => {
              clickTimer.current = null;
              onEditSwatch(value);
            }, 250);
          }}
          onDoubleClick={() => {
            if (disabled) return;
            cancelPendingClick();
            setEditing(true);
          }}
        >
          {value}
        </span>
      )}
      {!disabled && !editing && (
        <span
          className="tag-remove"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={onRemove}
        >
          <PlusSVG />
        </span>
      )}
    </div>
  );
};

export const TagValueInput: React.FC<TagValueInputProps> = ({
  values,
  onChange,
  placeholder = "Informe os valores separados por vírgula ou enter",
  helperText = "Informe os valores separados por vírgula ou enter. Use Tab para aceitar a sugestão. Exemplo: P, M, G.",
  disabled = false,
  label,
  onClear,
  dragHandleProps,
  suggestions = [],
  swatches = {},
  onEditSwatch,
}) => {
  const { theme } = useTheme();
  const [inputValue, setInputValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const safeSuggestions = Array.isArray(suggestions) ? suggestions : [];

  const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

  const isDuplicate = useCallback(
    (v: string) => values.some((existing) => existing.toLowerCase() === v.toLowerCase()),
    [values]
  );

  // Ghost text: primeira sugestão que começa com o que o user digitou
  const ghostSuggestion = useMemo(() => {
    if (!inputValue.trim() || safeSuggestions.length === 0) return null;
    const search = inputValue.toLowerCase();
    return safeSuggestions.find(
      (s) => s.toLowerCase().startsWith(search) && !isDuplicate(s)
    ) || null;
  }, [inputValue, safeSuggestions, isDuplicate]);

  const addValues = useCallback(
    (text: string) => {
      const newValues = text
        .split(",")
        .map((v) => capitalize(v.trim()))
        .filter((v) => v.length > 0 && !isDuplicate(v));
      if (newValues.length > 0) {
        onChange([...values, ...newValues]);
      }
    },
    [values, onChange]
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Tab" && ghostSuggestion) {
      e.preventDefault();
      if (!isDuplicate(ghostSuggestion)) {
        onChange([...values, capitalize(ghostSuggestion)]);
      }
      setInputValue("");
      return;
    }

    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      if (inputValue.trim()) {
        addValues(inputValue);
        setInputValue("");
      }
    } else if (e.key === "Backspace" && inputValue === "" && values.length > 0) {
      onChange(values.slice(0, -1));
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    if (val.includes(",")) {
      addValues(val);
      setInputValue("");
    } else {
      setInputValue(val);
    }
  };

  const handleBlur = () => {
    if (inputValue.trim()) {
      addValues(inputValue);
      setInputValue("");
    }
  };

  const removeTag = (index: number) => {
    onChange(values.filter((_, i) => i !== index));
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = values.indexOf(active.id as string);
      const newIndex = values.indexOf(over.id as string);
      onChange(arrayMove(values, oldIndex, newIndex));
    }
  };

  return (
    <TagValueInputContainer theme={theme}>
      {label && (
        <div className="tag-label">
          <span className="label-text fonteTexto">{label}</span>
          {onClear && (
            <div className="clear-btn" onClick={onClear} title="Remover nível">
              <PlusSVG />
            </div>
          )}
        </div>
      )}
      <div
        className={`tag-input-wrapper ${disabled ? "disabled" : ""} ${
          disabled && onEditSwatch ? "swatch-editavel" : ""
        }`}
        onClick={() => !disabled && inputRef.current?.focus()}
      >
        {dragHandleProps && (
          <span {...dragHandleProps} style={{ cursor: "grab", userSelect: "none", touchAction: "none" }}>
            ⠿
          </span>
        )}
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={values} strategy={horizontalListSortingStrategy}>
            {values.map((value, index) => (
              <SortableTag
                key={`${value}-${index}`}
                id={value}
                value={value}
                disabled={disabled}
                swatch={swatches[chaveSwatch(value)]}
                onEditSwatch={onEditSwatch}
                onRemove={() => removeTag(index)}
                onEdit={(newValue) => {
                  const updated = [...values];
                  updated[index] = newValue;
                  onChange(updated);
                }}
              />
            ))}
          </SortableContext>
        </DndContext>
        <span className="ghost-input-wrapper">
          <input
            ref={inputRef}
            type="text"
            className="tag-text-input fonteTexto"
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            placeholder={values.length === 0 ? placeholder : ""}
            disabled={disabled}
          />
          {ghostSuggestion && inputValue && (
            <span className="ghost-text fonteTexto">
              <span style={{ visibility: "hidden" }}>{inputValue}</span>
              {ghostSuggestion.slice(inputValue.length)}
            </span>
          )}
        </span>
      </div>
      {helperText && (
        <div className="tag-helper fonteRotulos">{helperText}</div>
      )}
    </TagValueInputContainer>
  );
};